import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import { bankOrFundManagerNameSchema } from "@/lib/bankOrFundManagerNameSchema";
import { InvestmentAccount, InvestmentFormData } from "@/hooks/useInvestments";
import { InvestmentType } from "@/hooks/useInvestmentTypes";
import { RiskRating } from "@/hooks/useRiskRatings";
import { useFundManagers, useCreateFundManager } from "@/hooks/useFundManagers";
import { CreatableSelect } from "@/components/creatable-select/CreatableSelect";
import { useCurrencyList } from "@/hooks/useCurrencyList";

const investmentFormSchema = z
  .object({
    fund_name: z
      .string()
      .min(1, "Fund name is required")
      .max(100, "Fund name must be 100 characters or less"),
    fund_manager: bankOrFundManagerNameSchema,
    investment_type_id: z.string().min(1, "Investment type is required"),
    currency: z.string().min(1, "Currency is required"),
    interest_rate: z
      .number()
      .min(0, "Interest rate must be 0 or greater")
      .max(100, "Interest rate must be 100 or less"),
    starting_amount: z
      .number()
      .positive("Starting amount must be greater than 0"),
    account_number: z
      .string()
      .min(1, "Account number is required")
      .max(50, "Account number must be 50 characters or less"),
    member_number: z
      .string()
      .max(50, "Member number must be 50 characters or less")
      .regex(
        /^[a-zA-Z0-9 -]*$/,
        "Member number may only contain letters, numbers, spaces, and hyphens",
      )
      .optional()
      .or(z.literal(""))
      .transform((v) => (v === "" ? undefined : v)),
    notes: z
      .string()
      .max(500, "Notes must be 500 characters or less")
      .optional(),
    description: z
      .string()
      .max(500, "Description must be 500 characters or less")
      .optional(),
    risk_rating_id: z.string().optional(),
  })
  .refine((data) => {
    return true;
  });

type InvestmentFormSchema = z.infer<typeof investmentFormSchema>;

interface InvestmentFormProps {
  investment?: InvestmentAccount;
  preselectedType?: string;
  investmentTypes: InvestmentType[];
  riskRatings?: RiskRating[];
  riskRatingsError?: boolean;
  onSave: (data: InvestmentFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

/**
 * InvestmentForm component for creating and editing investment accounts.
 * Supports custom investment types and validates all input fields.
 *
 * @param investment - Optional existing investment account for editing
 * @param preselectedType - Optional investment type ID to pre-select
 * @param investmentTypes - List of available investment types
 * @param onSave - Callback function when form is submitted with valid data
 * @param onCancel - Callback function when form is cancelled
 * @param isSubmitting - External submission state indicator
 *
 * @example
 * <InvestmentForm
 *   investment={existingInvestment}
 *   investmentTypes={types}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 * />
 */
export function InvestmentForm({
  investment,
  preselectedType,
  investmentTypes,
  riskRatings = [],
  riskRatingsError = false,
  onSave,
  onCancel,
  isSubmitting = false,
}: InvestmentFormProps) {
  const moderateRating = riskRatings.find((r) => r.name === "moderate");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting: formIsSubmitting },
  } = useForm<InvestmentFormSchema>({
    resolver: zodResolver(investmentFormSchema),
    defaultValues: {
      fund_name: investment?.fund_name ?? "",
      fund_manager: investment?.fund_manager ?? "",
      investment_type_id:
        investment?.investment_type_id ?? preselectedType ?? "",
      currency: investment?.currency ?? "USD",
      interest_rate: investment?.interest_rate ?? 0,
      starting_amount: investment?.starting_amount ?? 0,
      account_number: investment?.account_number ?? "",
      member_number: investment?.member_number ?? "",
      notes: investment?.notes ?? "",
      description: investment?.description ?? "",
      risk_rating_id: investment?.risk_rating_id ?? moderateRating?.id ?? "",
    },
  });

  const { data: fundManagers = [], isLoading: fmLoading } = useFundManagers();
  const createFundManager = useCreateFundManager();
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const investmentTypeId = watch("investment_type_id");
  const currency = watch("currency");
  const interestRate = watch("interest_rate");
  const startingAmount = watch("starting_amount");
  const fundManagerValue = watch("fund_manager");
  const riskRatingId = watch("risk_rating_id");

  const onSubmit = async (data: InvestmentFormSchema) => {
    await onSave(data as InvestmentFormData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="fund_name">Fund Name *</Label>
        <Input
          id="fund_name"
          {...register("fund_name")}
          placeholder="e.g. Growth Fund"
          maxLength={100}
        />
        {errors.fund_name && (
          <p className="text-sm text-destructive">{errors.fund_name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="fund_manager">Fund Manager *</Label>
        <CreatableSelect
          id="fund_manager"
          value={fundManagerValue}
          onValueChange={(val) => setValue("fund_manager", val)}
          options={fundManagers.map((fm) => ({
            value: fm.name,
            label: fm.name,
          }))}
          placeholder="Select or create a fund manager..."
          disabled={fmLoading}
          onCreate={async (name) => {
            const fm = await createFundManager.mutateAsync(name);
            return fm.name;
          }}
          aria-required={true}
          aria-describedby="fund_manager-error"
          aria-invalid={!!errors.fund_manager}
        />
        {errors.fund_manager && (
          <p id="fund_manager-error" className="text-sm text-destructive">
            {errors.fund_manager.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="investment_type_id">Investment Type *</Label>
        <Select
          value={investmentTypeId}
          onValueChange={(value) => {
            setValue("investment_type_id", value);
          }}>
          <SelectTrigger id="investment_type_id">
            <SelectValue placeholder="Select investment type" />
          </SelectTrigger>
          <SelectContent>
            {investmentTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.investment_type_id && (
          <p className="text-sm text-destructive">
            {errors.investment_type_id.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="risk_rating_id">Risk Rating</Label>
        <Select
          value={riskRatingId}
          onValueChange={(value) => setValue("risk_rating_id", value)}
          disabled={riskRatingsError}>
          <SelectTrigger id="risk_rating_id" disabled={riskRatingsError}>
            <SelectValue placeholder="Select risk rating" />
          </SelectTrigger>
          <SelectContent>
            {riskRatings.map((rating) => (
              <SelectItem key={rating.id} value={rating.id}>
                {rating.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {riskRatingsError && (
          <p className="text-sm text-destructive">
            Risk ratings could not be loaded.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="account_number">Account Number *</Label>
        <Input
          id="account_number"
          {...register("account_number")}
          placeholder="e.g. INV-123456"
          maxLength={50}
        />
        {errors.account_number && (
          <p className="text-sm text-destructive">
            {errors.account_number.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="member_number">
          Member Number{" "}
          <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <Input
          id="member_number"
          {...register("member_number")}
          placeholder="e.g. MEM-001"
          maxLength={50}
        />
        {errors.member_number && (
          <p className="text-sm text-destructive">
            {errors.member_number.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="currency">Currency *</Label>
          <Select
            value={currency}
            onValueChange={(value) => setValue("currency", value)}>
            <SelectTrigger id="currency">
              <SelectValue />
            </SelectTrigger>
            {isLoadingCurrencies && "Loading Currencies"}
            {!isLoadingCurrencies && !errorLoadingCurrencies ? (
              <SelectContent>
                {availableCurrencies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            ) : (
              "Failed to load currencies"
            )}
          </Select>
          {errors.currency && (
            <p className="text-sm text-destructive">
              {errors.currency.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="interest_rate">Interest Rate (% p.a.) *</Label>
          <Input
            id="interest_rate"
            type="number"
            step="0.01"
            min="0"
            max="100"
            {...register("interest_rate", { valueAsNumber: true })}
            placeholder="e.g. 5.5"
          />
          {errors.interest_rate && (
            <p className="text-sm text-destructive">
              {errors.interest_rate.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="starting_amount">Starting Amount *</Label>
        <Input
          id="starting_amount"
          type="number"
          step="0.01"
          min="0.01"
          {...register("starting_amount", { valueAsNumber: true })}
          placeholder="e.g. 10000"
        />
        {errors.starting_amount && (
          <p className="text-sm text-destructive">
            {errors.starting_amount.message}
          </p>
        )}
        {interestRate > 0 && startingAmount > 0 && (
          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              Estimated yearly interest:{" "}
              {(
                startingAmount *
                (Math.pow(1 + interestRate / 100 / 12, 12) - 1)
              ).toLocaleString(undefined, {
                style: "currency",
                currency,
              })}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Input
          id="description"
          {...register("description")}
          placeholder="Brief description of the investment"
          maxLength={500}
        />
        {errors.description && (
          <p className="text-sm text-destructive">
            {errors.description.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Additional notes..."
          rows={3}
          maxLength={500}
        />
        {errors.notes && (
          <p className="text-sm text-destructive">{errors.notes.message}</p>
        )}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting || formIsSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting || formIsSubmitting}>
          {isSubmitting || formIsSubmitting
            ? "Saving..."
            : investment
              ? "Update"
              : "Create"}
        </Button>
      </DialogFooter>
    </form>
  );
}
